import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { newsletterService } from '../../services';
import { useUser } from '../../context/UserContext';
import { useDialog } from '../../context/DialogContext';
import { AdminPageHeader, Btn, DataTable, shortDate, useAdminShell } from '../../components/admin/shell';
import NewsletterBuilderPage from '../NewsletterBuilderPage';

/**
 * MARKETING › NEWSLETTER — spec/sections.md §13. `/admin/newsletter` is the list of
 * issues; `/admin/newsletter/:id` (and `new`) is the builder as a full-width takeover:
 * the rail goes away, a crumb bar comes in, and the builder fills the main column.
 */
export default function NewsletterPage() {
  const { id } = useParams();
  return id ? <Takeover id={id} /> : <List />;
}

function List() {
  const navigate = useNavigate();
  const { can } = useUser();
  const { showConfirm, showAlert } = useDialog();
  const { refreshCounts } = useAdminShell();
  const canCreate = can('newsletter', 'create');
  const canDelete = can('newsletter', 'delete');
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    const r = await newsletterService.list();
    if (r.success) setRows(r.data || []);
    else setRows([]);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (n) => {
    const ok = await showConfirm({ title: 'DELETE DRAFT', message: 'This draft is removed. Sent issues are never deleted.', confirmLabel: 'DELETE', variant: 'danger', theme: 'admin' });
    if (!ok) return;
    const r = await newsletterService.remove(n.id);
    if (!r.success) await showAlert({ title: 'COULD NOT DELETE', message: r.error || 'Try again.', type: 'error', theme: 'admin' });
    load(); refreshCounts();
  };

  const drafts = (rows || []).filter((n) => n.status === 'Draft').length;
  const meta = rows == null ? 'Email issues.' : drafts === 0 ? 'Email issues. Nothing is unsent.' : `Email issues. ${drafts === 1 ? 'One draft is' : `${drafts} drafts are`} unsent.`;
  const count = (rows || []).length;

  return (
    <>
      <AdminPageHeader
        section="newsletter"
        meta={meta}
        actions={canCreate ? <Btn variant="white" onClick={() => navigate('/admin/newsletter/new')}>+ NEW NEWSLETTER</Btn> : null}
      />
      <DataTable
        columns={[
          { key: 'subject', label: 'SUBJECT', fr: 2.4, render: (n) => n.subject || '(no subject)' },
          { key: 'issueLabel', label: 'ISSUE', fr: 1.2, render: (n) => n.issueLabel || '—' },
          { key: 'status', label: 'STATUS', fr: 1, render: (n) => String(n.status || '').toUpperCase() },
          { key: 'recipientCount', label: 'AUDIENCE', fr: 1, right: true, render: (n) => (n.sentAt ? (n.recipientCount || 0).toLocaleString() : '—') },
          { key: 'sentAt', label: 'SENT', fr: 0.9, render: (n) => (n.sentAt ? shortDate(n.sentAt) : '—') },
          { key: 'opens', label: 'OPENS', fr: 0.8, right: true, render: () => '—' },
        ]}
        rows={rows || []}
        loading={rows == null}
        onRowClick={(n) => navigate(`/admin/newsletter/${n.id}`)}
        hasActions={canDelete}
        actions={(n) => (n.status === 'Draft' ? [{ label: 'DELETE', variant: 'danger', onClick: () => remove(n) }] : [])}
        footerLeft={`${count} ${count === 1 ? 'issue' : 'issues'}`}
      />
    </>
  );
}

function Takeover({ id }) {
  const navigate = useNavigate();
  const { setTakeover } = useAdminShell();
  const [, setState] = useState({ status: 'Draft', issueLabel: '', dirty: false, updatedAt: null, loading: true });

  useEffect(() => {
    setTakeover(true);
    return () => setTakeover(false);
  }, [setTakeover]);


  return (
    <div className="flex min-h-[calc(100vh-68px)] flex-col">
      <NewsletterBuilderPage embedded id={id} onState={setState} onBack={() => navigate('/admin/newsletter')} />
    </div>
  );
}
